import { createRootRouteWithContext, createRoute, createRouter, Navigate, Outlet, type ErrorComponentProps } from "@tanstack/react-router";
import { useStatus } from "@powersync/react";
import type { BootState } from "./Boot";
import { useTeams } from "@/data/queries";
import { Shell } from "./Shell";
import { Splash } from "@/ui/Splash";
import { HomePage } from "@/features/home/HomePage";
import { VisitsPage } from "@/features/visits/VisitsPage";
import { VisitPage } from "@/features/visits/VisitPage";
import { LocationsPage } from "@/features/locations/LocationsPage";
import { LocationPage } from "@/features/locations/LocationPage";
import { MapPage } from "@/features/map/MapPage";
import { ReportsPage } from "@/features/reports/ReportsPage";
import { TasksPage } from "@/features/tasks/TasksPage";
import { TeamActivityPage } from "@/features/dashboards/TeamActivityPage";
import { PipelinePage } from "@/features/dashboards/PipelinePage";
import { MetricsPage } from "@/features/dashboards/MetricsPage";
import { ClientsPage } from "@/features/clients/ClientsPage";
import { ClientPage } from "@/features/clients/ClientPage";
import { PaperTrailPage } from "@/features/clients/PaperTrailPage";
import { AskPage } from "@/features/ask/AskPage";
import { SettingsPage } from "@/features/settings/SettingsPage";

interface Ctx { boot: BootState }

const root = createRootRouteWithContext<Ctx>()({ component: () => <Outlet />, notFoundComponent: NotFound, errorComponent: RouteError });

// "/t" → the user's first team. Team lives in the URL so every link is shareable.
// Every SPA route sits under /t so the app can take over a host that Rails also
// serves (docs/cutover.md); "/" only forwards here.
function PickTeam() {
  const { data, isLoading } = useTeams();
  const status = useStatus();
  // An empty local DB means "not synced yet" until the first full sync lands.
  if (!data.length && (isLoading || !status.hasSynced)) return <Splash title="Syncing field data" />;
  if (!data.length) return <Splash title="No teams yet" detail="This account isn't a member of a team with synced data. Ask a team admin for an invite." />;
  return <Navigate to="/t/$teamId" params={{ teamId: data[0].id }} replace />;
}

// Replaces the router's generic "Something went wrong!" so the actual error and
// stack can be copied into a bug report.
function RouteError({ error: raw, reset }: ErrorComponentProps) {
  const error = raw as Error | undefined;
  console.error("[v2] route error", error);
  const detail = `${error?.message ?? String(error)}\n\n${window.location.pathname}\n${import.meta.env.VITE_APP_VERSION ?? "local"}\n\n${error?.stack ?? ""}`;
  return (
    <div className="route-error">
      <Splash title="This screen hit an error" detail={error?.message ?? String(error)} onRetry={() => { reset(); window.location.reload(); }} />
      <details className="route-error-detail"><summary>Error details</summary><pre>{detail}</pre>
        <button type="button" className="route-error-copy" onClick={() => navigator.clipboard?.writeText(detail)}>Copy details</button>
      </details>
    </div>
  );
}

function NotFound() {
  return <Splash title="Page not found" detail="The link may be old, or the record isn't synced to this device." />;
}

const index = createRoute({ getParentRoute: () => root, path: "/", component: () => <Navigate to="/t" replace /> });
const pickTeam = createRoute({ getParentRoute: () => root, path: "/t", component: PickTeam });
const team = createRoute({ getParentRoute: () => root, path: "/t/$teamId", component: Shell });
const home = createRoute({ getParentRoute: () => team, path: "/", component: HomePage });
const visits = createRoute({
  getParentRoute: () => team, path: "visits", component: VisitsPage,
  validateSearch: (s: Record<string, unknown>): { q?: string; status?: string; who?: string } => ({ q: (s.q as string) || undefined, status: (s.status as string) || undefined, who: (s.who as string) || undefined }),
});
const visit = createRoute({
  getParentRoute: () => team, path: "visits/$visitId", component: VisitPage,
  validateSearch: (s: Record<string, unknown>): { tab?: string } => ({ tab: (s.tab as string) || undefined }),
});
const locations = createRoute({
  getParentRoute: () => team, path: "locations", component: LocationsPage,
  validateSearch: (s: Record<string, unknown>): { q?: string; level?: string; view?: string; stale?: string } => ({ q: (s.q as string) || undefined, level: (s.level as string) || undefined, view: (s.view as string) || undefined, stale: (s.stale as string) || undefined }),
});
const location = createRoute({ getParentRoute: () => team, path: "locations/$locationId", component: LocationPage });
const map = createRoute({
  getParentRoute: () => team, path: "map", component: MapPage,
  validateSearch: (s: Record<string, unknown>): { level?: string } => ({ level: (s.level as string) || undefined }),
});
const reports = createRoute({
  getParentRoute: () => team, path: "reports", component: ReportsPage,
  validateSearch: (s: Record<string, unknown>): { status?: string } => ({ status: (s.status as string) || undefined }),
});
const tasks = createRoute({
  getParentRoute: () => team, path: "tasks", component: TasksPage,
  validateSearch: (s: Record<string, unknown>): { scope?: string; view?: string; new?: string; task?: string } => ({ scope: (s.scope as string) || undefined, view: (s.view as string) || undefined, new: (s.new as string) || undefined, task: (s.task as string) || undefined }),
});
// v1 name kept as a redirect; Contacts are now the Client portfolio.
const contacts = createRoute({ getParentRoute: () => team, path: "contacts", component: () => <Navigate to="/t/$teamId/clients" params={(p: any) => p} replace /> });
const range = (s: Record<string, unknown>): { range?: string; who?: string } => ({ range: (s.range as string) || undefined, who: (s.who as string) || undefined });
const dashTeam = createRoute({ getParentRoute: () => team, path: "dashboards/team", component: TeamActivityPage, validateSearch: range });
const dashPipeline = createRoute({ getParentRoute: () => team, path: "dashboards/pipeline", component: PipelinePage, validateSearch: range });
const dashMetrics = createRoute({ getParentRoute: () => team, path: "dashboards/metrics", component: MetricsPage,
  validateSearch: (s: Record<string, unknown>): { range?: string; metric?: string; location?: string } => ({ range: (s.range as string) || undefined, metric: (s.metric as string) || undefined, location: (s.location as string) || undefined }) });
const dashboards = createRoute({ getParentRoute: () => team, path: "dashboards", component: () => <Navigate to="/t/$teamId/dashboards/team" params={(p: any) => p} replace /> });
const clients = createRoute({ getParentRoute: () => team, path: "clients", component: ClientsPage, validateSearch: (s: Record<string, unknown>): { q?: string } => ({ q: (s.q as string) || undefined }) });
const paperTrail = createRoute({ getParentRoute: () => team, path: "clients/paper-trail", component: PaperTrailPage,
  validateSearch: (s: Record<string, unknown>): { q?: string; location?: string; type?: string } => ({ q: (s.q as string) || undefined, location: (s.location as string) || undefined, type: (s.type as string) || undefined }) });
const client = createRoute({ getParentRoute: () => team, path: "clients/$clientId", component: ClientPage });
const ask = createRoute({ getParentRoute: () => team, path: "ask", component: AskPage,
  validateSearch: (s: Record<string, unknown>): { q?: string; at?: string } => ({ q: (s.q as string) || undefined, at: (s.at as string) || undefined }) });
const settings = createRoute({
  getParentRoute: () => team, path: "settings", component: SettingsPage,
  validateSearch: (s: Record<string, unknown>): { section?: string } => ({ section: (s.section as string) || undefined }),
});

const tree = root.addChildren([index, pickTeam, team.addChildren([home, visits, visit, locations, location, map, reports, tasks, contacts, settings, dashboards, dashTeam, dashPipeline, dashMetrics, clients, paperTrail, client, ask])]);

export const router = createRouter({ routeTree: tree, context: { boot: undefined! }, defaultPreload: "intent" });

declare module "@tanstack/react-router" {
  interface Register { router: typeof router }
}
